import * as moment from 'moment';
import { useContext, useState } from 'react';
import { downloadFile } from '@nkr/services/download-file';
import { toast } from 'react-toastify';
import { FileDocumentType } from '@nkr/enums/document-type';
import { PatientIntakeTabs } from './nav';
import { PatientIntakeContext } from './context';
import OverlayMenuButton, { IMenuItem } from '@nkr/components/overlay-menu-button';
import UploadAboModal from './upload-abo-modal';

export enum WorkflowAction {
    View = 1,
    DownloadQuestionnaire = 2,
    DownloadAbo = 3,
    SendPasswordReset = 4,
    UploadAbo = 5,
    TransferPatient = 6,
    Divider = -1,
}

const menuItemList = [
    { action: WorkflowAction.View, title: 'View Referral' },
    { action: WorkflowAction.DownloadQuestionnaire, title: 'Download Questionnaire' },
    { action: WorkflowAction.DownloadAbo, title: 'View Lab Results' },
    { action: WorkflowAction.SendPasswordReset, title: 'Send Password Reset' },
    { action: WorkflowAction.UploadAbo, title: 'Upload Lab Results' },
    { action: WorkflowAction.TransferPatient, title: 'Transfer Patient' },
    { action: WorkflowAction.Divider, title: '' }
];

interface IProps {
    hasAboResults: boolean,
    hasPendingPreWorkupLabs: boolean,
    medicalRecordId: number,
    patientScreenId: number,
    firstName: string,
    lastName: string,
    allowTransfer: boolean,
    isList: boolean,
    onSave?: () => void
}

const WorkflowActionsDropdown = (props: IProps) => {
    const context = useContext(PatientIntakeContext);
    
    const [showUploadModal, setShowUploadModal] = useState(false);

    const handleMenuToggle = (show: boolean) => {
        // Handle menu toggle if needed
    };

    const handleMenuSelection = (id: any, action: number) => {
        switch (action) {
            case WorkflowAction.DownloadQuestionnaire:
                context.onGetQuestionaireReport(props.medicalRecordId)
                    .then((response: any) => {
                        let fileName = props.firstName + '_' + props.lastName
                            + "_Questionnaire"
                            + "_" + moment().format("YYYY_MM_DD");

                        downloadFile(response.data, fileName, response.data.type);
                    });
                break;

            case WorkflowAction.DownloadAbo:
                context.onDownloadFile(FileDocumentType.PsAboTyping, props.patientScreenId)
                    .then((response: any) => {
                        let fileName = props.firstName + '_' + props.lastName
                            + "_" + FileDocumentType[FileDocumentType.PsAboTyping].toString()
                            + "_" + moment().format("YYYY_MM_DD");

                        downloadFile(response.data, fileName, response.data.type);
                    });
                break;

            case WorkflowAction.SendPasswordReset:
                context.onPasswordResetEmail(props.medicalRecordId)
                    .then(() => {
                        toast.success("Password reset email sent successfully.");
                    })
                    .catch((error: any) => {
                        toast.error("Failed to send password reset email: " + error.message);
                    });
                break;

            case WorkflowAction.View:
                context.onView(props.medicalRecordId);
                break;

            case WorkflowAction.UploadAbo:
                setShowUploadModal(true);
                break;

            case WorkflowAction.TransferPatient:
                context.onToggleTransfer(props.medicalRecordId, true);
                break;
        }
    };

    const handleUploadSubmit = (file: File) => {
        context.onUploadAboResults(props.medicalRecordId, file)
            .then(() => {
                toast.success("ABO results uploaded successfully.");
                setShowUploadModal(false);
            })
            .catch((error: any) => {
                toast.error("Failed to upload ABO results: " + error.message);
            });
    };

    const handleUploadCancel = () => {
        setShowUploadModal(false);
    };

    const getMenuItems = () => {
        let menuItems: number[] = [];

        // Only show View in grid view (when onSave is not provided)
        if (!props.onSave) {
            menuItems.push(WorkflowAction.View);
        }

        menuItems.push(WorkflowAction.DownloadQuestionnaire);

        if (props.hasAboResults) {
            menuItems.push(WorkflowAction.DownloadAbo);
        } else if (props.hasPendingPreWorkupLabs && context.currentTab != PatientIntakeTabs.PENDING_ORDERS) {
            menuItems.push(WorkflowAction.UploadAbo);
        }

        menuItems.push(WorkflowAction.SendPasswordReset);

        // if (props.allowTransfer) {
        //     menuItems.push(-1);
        //     menuItems.push(WorkflowAction.TransferPatient);
        // }

        return menuItems;
    };

    const menu = getMenuItems().map(item => {
        let menuItem = menuItemList.find(x => x.action === item);

        if (!menuItem) {
            return null;
        }

        const id = props.medicalRecordId;

        return {
            id,
            action: item,
            include: true,
            selectable: true,
            element: <>{menuItem.title}</>,
            title: menuItem.title,
            divider: item == WorkflowAction.Divider
        };
    }).filter(item => item !== null) as IMenuItem[];

    return (
        <div>
            <OverlayMenuButton
                menu={menu}
                bsStyle='primary'
                bsSize={props.isList ? 'xs' : 'xs'}
                title='Actions'
                listItemStyle={{ padding: '2px 10px', fontSize: 14 }}
                includeCaret={true}
                onToggle={handleMenuToggle}
                onSelect={handleMenuSelection}
            />

            <UploadAboModal
                show={showUploadModal}
                fullName={`${props.firstName} ${props.lastName}`}
                onSubmit={handleUploadSubmit}
                onCancel={handleUploadCancel}
            />
        </div>
    );
};

export default WorkflowActionsDropdown;
